import React, { useMemo } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import SimpleStatistics from 'components/SimpleStatistics/SimpleStatistics';
import PATHS from 'constants/paths';
import FullScreen from 'containers/FullScreen/FullScreen';
import useExercisesStore from 'storages/exercises/exercises';
import useSheetsStore from 'storages/sheets/sheets';
import useSubjectStore from 'storages/subject';
import SubjectSelectionTile from 'uniqueComponents/Dashboard/SubjectSelectionTile/SubjectSelectionTile';
import DashboardCard from 'uniqueComponents/Dashboard/DashboardCard/DashboardCard';
import { useQuery } from 'react-query';
import QUERY from 'constants/queryKeys';
import usersApi from 'api/rest/users/users';
import { useAuthContext } from 'context/AuthContext';
import useOnGoAvailability from 'hooks/useOnGoAvailability/useOnGoAvailability';

import exercisesImageSrc from 'assets/icons/dashboard/exercises.png';
import ongoImageSrc from 'assets/icons/dashboard/ongo.png';
import setsImageSrc from 'assets/icons/dashboard/sets.png';
import sheetsImageSrc from 'assets/icons/dashboard/sheets.png';
import teacherImageSrc from 'assets/icons/dashboard/teacher.png';

const useStyles = makeStyles(theme => ({
  main: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    maxWidth: '950px',
    padding: theme.legacySpacing(0, 6),
    width: '100%',
    gridGap: '50px',
    alignItems: 'stretch',
    paddingTop: '2rem',

    '@media(max-width: 740px)': {
      gridTemplateColumns: '1fr',
      gridGap: '25px',
    },
  },
  rowCell: {
    display: 'grid',
    gridGap: '50px',
    '@media(max-width: 740px)': {
      gridGap: '25px',
    },
  },
  iconDisabled: {
    opacity: 0.38,
  },
}));

const DashboardContent = () => {
  const navigate = useNavigate();
  const { subject } = useSubjectStore();
  const sheetsStore = useSheetsStore();
  const exercisesStore = useExercisesStore();
  const { isTeacher } = useAuthContext();
  const { onGoAdditionalText, isOnGoDisabled } = useOnGoAvailability();

  const { data } = useQuery(QUERY.GET_ME, usersApi.getMe());
  const hasPremiumAccess = useMemo(() => data?.hasPremiumAccess, [data]);

  const classes = useStyles();
  if (!subject) {
    sheetsStore.reset();
    exercisesStore.reset();
    return navigate(PATHS.SUBJECT_SELECT);
  }
  return (
    <FullScreen>
      <div className={classes.main}>
        <SubjectSelectionTile />
        <SimpleStatistics />
        <div className={classes.rowCell}>
          <DashboardCard
            ariaLabel='Przeglądaj zadania'
            denseMobile
            description='Przeszukuj bazę zadań maturalnych według działów, filtrów i swoich oznaczeń.'
            heading='Przeglądaj zadania'
            layout='feature'
            to={PATHS.EXERCISES}
          >
            <img alt='' src={exercisesImageSrc} />
          </DashboardCard>
          <DashboardCard
            additionalText={!hasPremiumAccess ? '⭐ Premium' : null}
            ariaLabel='Rozwiązuj arkusze'
            denseMobile
            description='Pełne arkusze maturalne z poprzednich lat. Ćwicz jak na prawdziwej maturze.'
            disabled={!hasPremiumAccess}
            heading='Rozwiązuj arkusze'
            layout='feature'
            to={PATHS.SHEETS}
          >
            <img alt='' className={clsx(!hasPremiumAccess && classes.iconDisabled)} src={sheetsImageSrc} />
          </DashboardCard>
        </div>
        {isTeacher ? (
          <DashboardCard
            additionalText={!hasPremiumAccess ? '⭐ Premium' : null}
            ariaLabel='Panel nauczyciela'
            denseMobile
            description='Przeglądaj postępy całej klasy i zadania oznaczone przez uczniów.'
            disabled={!hasPremiumAccess}
            heading='Panel nauczyciela'
            layout='feature'
            to={PATHS.TEACHER_DASHBOARD}
          >
            <img alt='' className={clsx(!hasPremiumAccess && classes.iconDisabled)} src={teacherImageSrc} />
          </DashboardCard>
        ) : (
          <DashboardCard
            additionalText={onGoAdditionalText}
            ariaLabel='Rozwiązuj w głowie'
            denseMobile
            description='Szybkie powtórki w dowolnym momencie - w autobusie, w kolejce albo czekając na pizzę.'
            disabled={isOnGoDisabled}
            heading='Rozwiązuj w głowie'
            layout='feature'
            to={PATHS.ON_GO}
          >
            <img alt='' className={clsx(isOnGoDisabled && classes.iconDisabled)} src={ongoImageSrc} />
          </DashboardCard>
        )}

        <DashboardCard
          additionalText={!hasPremiumAccess ? '⭐ Premium' : null}
          ariaLabel='Twoje zestawy zadań'
          denseMobile
          description='Przeglądaj i twórz własne zestawy zadań.'
          disabled={!hasPremiumAccess}
          heading='Twoje zestawy zadań'
          layout='feature'
          to={PATHS.EXERCISES_SETS}
        >
          <img alt='' className={clsx(!hasPremiumAccess && classes.iconDisabled)} src={setsImageSrc} />
        </DashboardCard>
      </div>
    </FullScreen>
  );
};

export default DashboardContent;
